import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import apiService from '../../services/ApiService';
import type { PublicEventData } from '../../types/event';
import PageLoader from '../ui/PageLoader';
import Footer from '../Footer';
import PageBackLink from '../ui/PageBackLink';
import { CalendarIcon, MapPinIcon } from '@heroicons/react/24/outline';
import CommunityPostFeed from '../community/CommunityPostFeed';
import RegistrationStatusPanel from './RegistrationStatusPanel';
import { entryModeDescription } from '../../utils/registrationStatus';

function formatEventDateTime(startAt: string | null, endAt: string | null): string {
  if (!startAt) return 'Date TBD';
  const start = new Date(startAt.replace(' ', 'T'));
  if (isNaN(start.getTime())) return 'Date TBD';
  const startStr = start.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  if (!endAt) return startStr;
  const end = new Date(endAt.replace(' ', 'T'));
  if (isNaN(end.getTime())) return startStr;
  const endStr = end.toLocaleString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${startStr} to ${endStr}`;
}

function formatLocation(event: PublicEventData['event']): string | null {
  const parts = [event.location, [event.city, event.state].filter(Boolean).join(', ')].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

const PublicEventPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<PublicEventData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!slug) return;
    const result = await apiService.getPublicEventBySlug(slug);
    setData(result);
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        await reload();
        if (slug) {
          const result = await apiService.getPublicEventBySlug(slug);
          document.title = `${result.event.title} · Bilt`;
        }
      } catch (err: any) {
        setError(err.message || 'Event not found');
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, reload]);

  if (loading) {
    return (
      <>
        <PageLoader message="Loading event…" />
        <Footer />
      </>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <p className="kicker mb-3">Event</p>
            <h1 className="heading-section mb-2">Event not found</h1>
            <p className="text-muted mb-6 text-sm leading-relaxed">
              {error || 'This event may be unpublished or no longer available.'}
            </p>
            <Link to="/events" className="btn-primary min-h-[44px] inline-flex">
              Browse events
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const { event, classes, viewer, updates, my_registrations, pricing, feature_flags } = data;
  const location = formatLocation(event);
  const activeRegistrations = my_registrations || [];
  const hasActiveRegistration = activeRegistrations.length > 0;
  const canRegister = classes.length > 0 && !viewer?.is_registered && !hasActiveRegistration;
  const canViewUpdates = Boolean(viewer?.can_view_updates);
  const canPostUpdates = Boolean(viewer?.can_post_updates);
  const ticketsEnabled = Boolean(feature_flags?.spectator_tickets_enabled);
  const isCurated = (event.entry_mode || 'open_free').startsWith('pre_approval');

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <div className="flex-1 max-w-2xl mx-auto w-full px-4 sm:px-6 py-8 sm:py-10">
        <PageBackLink label="Back to Events" to="/events" className="mb-4" />
        {event.flyer_path ? (
          <div className="mb-6 -mx-4 sm:mx-0 sm:rounded-2xl overflow-hidden border-0 sm:border border-white/[0.06] bg-[#0a0a0a]">
            <img
              src={apiService.getFileUrl(event.flyer_path) || event.flyer_path}
              alt=""
              className="w-full aspect-[16/10] object-cover"
            />
          </div>
        ) : null}
        <header className="mb-8 pb-6 border-b border-white/[0.06]">
          <p className="kicker mb-3">Event</p>
          <h1 className="heading-display leading-tight">{event.title}</h1>
          <div className="mt-5 space-y-2.5 text-muted text-sm sm:text-base">
            <div className="flex items-start gap-3">
              <CalendarIcon className="w-5 h-5 shrink-0 mt-0.5 text-gray-500" aria-hidden />
              <span>{formatEventDateTime(event.start_at, event.end_at)}</span>
            </div>
            {location && (
              <div className="flex items-start gap-3">
                <MapPinIcon className="w-5 h-5 shrink-0 mt-0.5 text-gray-500" aria-hidden />
                <span>{location}</span>
              </div>
            )}
          </div>
        </header>

        {event.description && (
          <div className="mb-4 text-muted leading-relaxed whitespace-pre-wrap text-sm sm:text-base">
            {event.description}
          </div>
        )}

        <p className="text-xs text-gray-500 mb-6">{entryModeDescription(event.entry_mode)}</p>

        <section className="mb-8">
          <h2 className="heading-section mb-3">Judged classes</h2>
          {classes.length === 0 ? (
            <p className="text-muted text-sm">Classes will be posted before registration opens.</p>
          ) : (
            <ul className="divide-y divide-white/[0.06] border border-white/[0.06] rounded-xl overflow-hidden">
              {classes.map((cls) => (
                <li key={cls.id} className="px-4 py-3.5 bg-[#0c0c0c]/40">
                  <p className="font-medium text-white">{cls.name}</p>
                  {cls.description && (
                    <p className="text-sm text-gray-500 mt-0.5">{cls.description}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {activeRegistrations.map((reg) => (
          <RegistrationStatusPanel
            key={reg.id}
            event={event}
            registration={reg}
            pricing={pricing}
            onUpdated={() => void reload()}
          />
        ))}

        <section className="card p-5 sm:p-6 mb-8">
          <h2 className="heading-card mb-2">
            {hasActiveRegistration || viewer?.is_registered ? 'Vehicle entry' : 'Register your vehicle'}
          </h2>
          {isCurated && (
            <p className="text-muted text-sm mb-3 leading-relaxed">
              This is a curated show. Applications are reviewed before confirmation
              {(event.entry_mode || '').includes('paid')
                ? ', and payment is only requested after approval.'
                : '.'}
            </p>
          )}
          {hasActiveRegistration || viewer?.is_registered ? (
            <p className="text-muted text-sm leading-relaxed">
              Your current status is above. Entrant updates from the organizer appear below.
            </p>
          ) : canRegister ? (
            <>
              <p className="text-muted text-sm mb-5 leading-relaxed">
                Pick a vehicle from your garage. Your build profile travels with you. No duplicate entry.
              </p>
              <button
                type="button"
                onClick={() => navigate(`/events/${event.slug}/register`)}
                className="btn-primary min-h-[44px] w-full sm:w-auto"
              >
                Register your vehicle
              </button>
            </>
          ) : (
            <p className="text-muted text-sm">Check back once classes are posted.</p>
          )}
        </section>

        {ticketsEnabled ? (
          <section className="card p-5 sm:p-6 mb-8">
            <h2 className="heading-card mb-2">Spectator tickets</h2>
            <p className="text-muted text-sm mb-5 leading-relaxed">
              Purchase admission for spectators. Vehicle entrants register separately.
            </p>
            <Link to={`/events/${event.slug}/tickets`} className="btn-secondary min-h-[44px] inline-flex">
              Buy tickets
            </Link>
          </section>
        ) : null}

        <div className="mb-10">
          <CommunityPostFeed
            scope="event"
            entityId={Number(event.id)}
            canView={canViewUpdates}
            canPost={canPostUpdates}
            initialItems={updates?.items || []}
            initialTotal={Number(updates?.total || 0)}
            lockedHint="Register a vehicle for this event to read organizer updates about schedule, logistics, and day-of notes."
            emptyMemberHint={
              canPostUpdates
                ? 'Post schedule changes and logistics so every registered entrant stays informed.'
                : 'When the organizer posts, you’ll see it here.'
            }
          />
        </div>

        <div className="text-center pt-4 border-t border-white/[0.06]">
          <Link
            to={`/events/${event.slug}/archive`}
            className="text-sm text-muted hover:text-white min-h-[44px] inline-flex items-center"
          >
            View event archive →
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PublicEventPage;
